import availabilityOverrides, { optionKeys } from '../../components/options/OptionsAvailabilityOverrides';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import parentTestService from '../../services/ParentTestService';
import readinessCheckService from '../../services/ReadinessCheckService';
import { pollyVoiceLanguageMap } from '../../services/TextToSpeechService';
import i18nService from '../../services/i18nService';
import apiReader from '../../store/readHelpers/ApiReader';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import timeRemainingReader from '../../store/readHelpers/TimeReader';

const optionsListSelector = {
    selectIsAddExtraTimeAvailable() {
        return availabilityOverrides.isAvailableWithCurrentTheme(optionKeys.addExtraTime)
            && sessionReader.isOfflineMode()
            && sessionReader.isContextExam();
    },

    selectIsHideTimeRemainingAvailable() {
        return availabilityOverrides.isAvailableWithCurrentTheme(optionKeys.hideRemainingTime)
            && (sessionReader.isContextExam() || sessionReader.isContextPreview());
    },

    selectIsLanguageChooserAvailable() {
        return availabilityOverrides.isAvailableWithCurrentTheme(optionKeys.language) && i18nService.getAvailableLanguages().length > 1;
    },

    selectIsContrastChooserAvailable(ui) {
        const { a11yContrastOptions = [] } = ui;
        return a11yContrastOptions.length > 1;
    },

    selectIsTTSAvailable() {
        const lang = sessionReader.getLanguage();
        return settingsReader.isTTSEnabled() && pollyVoiceLanguageMap[lang];
    },

    selectIsFileDeliveryOptionAvailable(uiVariables) {
        if (uiVariables.options__moveFileDeliveryToInvigilator) {
            return false;
        }
        if (sessionReader.isPublicExam()) {
            return false;
        }
        if (sessionReader.isParentTestContextOutsideChildTest()) {
            return false;
        }
        if (this.isTestPendingToBeStarted()) {
            return false;
        }
        return sessionReader.isContextExam()
            && !sessionReader.userHasChromeKiosk()
            && !marketplacePropertiesService.isDisableSaveSubmissionFile();
    },

    isTestPendingToBeStarted() {
        return sessionReader.isTestThatRequiresPreStartStepAndSittingHasNotYetStarted();
    },

    selectIsDeliverBlankOptionAvailable() {
        if (!apiReader.isOnline()) {
            return false;
        }
        if (sessionReader.isPublicExam()) {
            return false;
        }
        if (this.isTestPendingToBeStarted()) {
            return false;
        }
        return sessionReader.isContextExam()
            && availabilityOverrides.isAvailableWithCurrentTheme(optionKeys.deliverBlank)
            && settingsReader.isDeliverBlankEnabled();
    },

    selectIsWithdrawOptionAvailable() {
        if (!apiReader.isOnline()) {
            return false;
        }
        if (sessionReader.isPublicExam()) {
            return false;
        }
        if (this.isTestPendingToBeStarted()) {
            return false;
        }
        return sessionReader.isContextExam()
            && availabilityOverrides.isAvailableWithCurrentTheme(optionKeys.withdraw)
            && settingsReader.isWithdrawEnabled();
    },

    selectIsBackToDashboardOptionAvailable() {
        if (
            !availabilityOverrides.isAvailableWithCurrentTheme(optionKeys.backToDashboard)
            || !apiReader.isOnline()
            || sessionReader.isPublicExam()
            || timeRemainingReader.timeHasExpired()
            || sessionReader.userHasLockDown()
        ) {
            return false;
        }
        if (this.isTestPendingToBeStarted()) {
            return false;
        }

        return sessionReader.isContextExam() || sessionReader.isContextViewOwnDelivery();
    },

    selectIsInstructionsOptionAvailable() {
        const assessment = sessionReader.getAssessmentRunId();
        return parentTestService.hasChild(assessment);
    },

    selectIsDeliverOptionAvailable() {
        return sessionReader.isContextExam()
            && (!sessionReader.isAutoDeliveringEnabled() || sessionReader.isAutoDeliveringWithManualEnabled())
            && !sessionReader.isAutoDeliverHighStakesEnabled()
            && !sessionReader.isContextReadinessCheck()
            && !readinessCheckService.isReadinessCheckEnabledButIncomplete()
            && assessmentReader.isAssessmentLoaded()
            && !sessionReader.isParentTestContextOutsideChildTest()
            && !sessionReader.isRemoteOralExam()
            && !settingsReader.isOneWayNavigationEnabled();
    },

    selectIsInvigilatorActionsOptionAvailable() {
        return sessionReader.testUsesInvigilatorPassword()
            && !marketplacePropertiesService.isDisableSaveSubmissionFile();
    },

    selectProps(state) {
        const {
            ui,
            currentlyShown,
            api: { isOnline },
        } = state;

        return {
            currentlyShown,
            isAddExtraTimeAvailable: this.selectIsAddExtraTimeAvailable(),
            isBackToDashboardOptionAvailable: this.selectIsBackToDashboardOptionAvailable(),
            isContrastChooserAvailable: this.selectIsContrastChooserAvailable(ui),
            isDeliverBlankOptionAvailable: this.selectIsDeliverBlankOptionAvailable(),
            isDeliverOptionAvailable: this.selectIsDeliverOptionAvailable(),
            isExitChromeAppAvailable: sessionReader.userHasChromeKiosk(),
            isExitSebOptionAvailable: sessionReader.userHasSeb(),
            isFileDeliveryChromeAppOptionAvailable: sessionReader.userHasChromeKiosk(),
            isFileDeliveryOptionAvailable: this.selectIsFileDeliveryOptionAvailable(ui.variables),
            isHideTimeRemainingAvailable: this.selectIsHideTimeRemainingAvailable(),
            isInstructionsOptionAvailable: this.selectIsInstructionsOptionAvailable(),
            isInvigilatorActionsOptionAvailable: this.selectIsInvigilatorActionsOptionAvailable(),
            isLanguageChooserAvailable: this.selectIsLanguageChooserAvailable(),
            isOnline,
            isResumePasswordOptionAvailable: sessionReader.isRequiringPasswordForResume() || false,
            isTTSAvailable: this.selectIsTTSAvailable(),
            isWithdrawOptionAvailable: this.selectIsWithdrawOptionAvailable(),
            isZoomFactorChooserAvailable: true,
            ui,
        };
    },
};

export default optionsListSelector;
